import { useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import SupportBundleButton from "../../components/system/SupportBundleButton";
import StateAchievementsExportPanel from "../../components/state/StateAchievementsExportPanel";
type AnyRow = Record<string, any>;

function loadBulkDiscordPresets(): any[] {
  try {
    const raw = localStorage.getItem("bulkDiscordPresets");
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveBulkDiscordPresets(arr: any[]) {
  try {
    localStorage.setItem("bulkDiscordPresets", JSON.stringify(Array.isArray(arr) ? arr : []));
  } catch {}
}

function nowUtc() { return new Date().toISOString(); }
function norm(s: any) { return String(s || "").trim(); }
function normLower(s: any) { return String(s || "").trim().toLowerCase(); }
function asInt(v: any, fallback: number) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.floor(n) : fallback;
}

async function safeRpcBool(name: string): Promise<boolean> {
  try {
    const r = await supabase.rpc(name as any);
    if (r.error) return false;
    return r.data === true;
  } catch {
    return false;
  }
}

export default function OwnerStateAchievementsPage() {
  const location = useLocation();
  const [stateCode, setStateCode] = useState("789");
  const [tab, setTab] = useState<"requests" | "types" | "options" | "access" | "export">("requests");

  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<string | null>(null);

  const [userId, setUserId] = useState<string | null>(null);
  const [isAppAdmin, setIsAppAdmin] = useState(false);
  const [isDashboardOwner, setIsDashboardOwner] = useState(false);

  const [types, setTypes] = useState<AnyRow[]>([]);
  const [options, setOptions] = useState<AnyRow[]>([]);
  const [requests, setRequests] = useState<AnyRow[]>([]);
  const [access, setAccess] = useState<AnyRow[]>([]);
  const [requestAllianceFilter, setRequestAllianceFilter] = useState("ALL");
  const [requestPlayerFilter, setRequestPlayerFilter] = useState("");
  const [requestTypeFilter, setRequestTypeFilter] = useState("ALL");
  const [requestStatusFilter, setRequestStatusFilter] = useState("ALL");
  const initialAllianceFromQuery = useMemo(() => {
    const p = new URLSearchParams(location.search || "");
    return String(p.get("alliance") || "").trim().toUpperCase();
  }, [location.search]);

  const initialTypeFromQuery = useMemo(() => {
    const p = new URLSearchParams(location.search || "");
    return String(p.get("type") || "").trim();
  }, [location.search]);

  const initialPlayerFromQuery = useMemo(() => {
    const p = new URLSearchParams(location.search || "");
    return String(p.get("player") || "").trim();
  }, [location.search]);

  const initialStatusFromQuery = useMemo(() => {
    const p = new URLSearchParams(location.search || "");
    return String(p.get("status") || "").trim().toLowerCase();
  }, [location.search]);
  const [selectedRequestIds, setSelectedRequestIds] = useState<string[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkDiscordAlliance, setBulkDiscordAlliance] = useState("WOC");
  const [bulkDiscordWebhookId, setBulkDiscordWebhookId] = useState("");
  const [bulkDiscordWebhooks, setBulkDiscordWebhooks] = useState<AnyRow[]>([]);
  const [bulkDiscordPreview, setBulkDiscordPreview] = useState("");
  const [bulkDiscordMessageStyle, setBulkDiscordMessageStyle] = useState("detailed");
  const [bulkDiscordTargetMode, setBulkDiscordTargetMode] = useState("grouped");
  const [bulkIncludeSubmitted, setBulkIncludeSubmitted] = useState(true);
  const [bulkIncludeInProgress, setBulkIncludeInProgress] = useState(true);
  const [bulkIncludeCompleted, setBulkIncludeCompleted] = useState(true);
  const [bulkDiscordPresets, setBulkDiscordPresets] = useState<any[]>([]);
  const [selectedBulkDiscordPreset, setSelectedBulkDiscordPreset] = useState("");
  const [newBulkDiscordPresetName, setNewBulkDiscordPresetName] = useState("");

  const allianceOptions = useMemo(() => {
    const vals = Array.from(new Set(requests.map((r) => String(r?.alliance_name || r?.alliance_code || "").trim().toUpperCase()).filter(Boolean)));
    return ["ALL", ...vals.sort((a, b) => a.localeCompare(b))];
  }, [requests]);

  const playerOptions = useMemo(() => {
    const vals = Array.from(new Set(requests.map((r) => String(r?.player_name || "").trim()).filter(Boolean)));
    return vals.sort((a, b) => a.localeCompare(b));
  }, [requests]);

  const typeById = useMemo(() => {
    const m: Record<string, AnyRow> = {};
    for (const t of types) if (t?.id) m[String(t.id)] = t;
    return m;
  }, [types]);

  const typeOptions = useMemo(() => {
    const vals = Array.from(new Set(requests.map((r) => {
      const t = typeById[String(r?.achievement_type_id || "")];
      return String(t?.name || "");
    }).filter(Boolean)));
    return ["ALL", ...vals.sort((a, b) => a.localeCompare(b))];
  }, [requests, typeById]);

  const statusOptions = useMemo(() => {
    const vals = Array.from(new Set(requests.map((r) => String(r?.status || "").trim()).filter(Boolean)));
    return ["ALL", ...vals.sort((a, b) => a.localeCompare(b))];
  }, [requests]);

  const filteredRequests = useMemo(() => {
    return requests.filter((r) => {
      const alliance = String(r?.alliance_name || r?.alliance_code || "").trim().toUpperCase();
      const player = String(r?.player_name || "").trim().toLowerCase();
      const typeName = String(typeById[String(r?.achievement_type_id || "")]?.name || "").trim();
      const status = String(r?.status || "").trim();

      const allianceOk = requestAllianceFilter === "ALL" || alliance === requestAllianceFilter;
      const playerOk = !String(requestPlayerFilter || "").trim() || player.includes(String(requestPlayerFilter || "").trim().toLowerCase());
      const typeOk = requestTypeFilter === "ALL" || typeName === requestTypeFilter;
      const statusOk = requestStatusFilter === "ALL" || status === requestStatusFilter;

      return allianceOk && playerOk && typeOk && statusOk;
    });
  }, [requests, typeById, requestAllianceFilter, requestPlayerFilter, requestTypeFilter, requestStatusFilter]);

  const requestActiveFilterSummary = useMemo(() => {
    const parts: string[] = [];
    if (requestAllianceFilter !== "ALL") parts.push(`Alliance: ${requestAllianceFilter}`);
    if (String(requestPlayerFilter || "").trim()) parts.push(`Player: ${String(requestPlayerFilter || "").trim()}`);
    if (requestTypeFilter !== "ALL") parts.push(`Type: ${requestTypeFilter}`);
    if (requestStatusFilter !== "ALL") parts.push(`Status: ${requestStatusFilter}`);
    return parts;
  }, [requestAllianceFilter, requestPlayerFilter, requestTypeFilter, requestStatusFilter]);

  const optionsByType = useMemo(() => {
    const m: Record<string, AnyRow[]> = {};
    for (const o of options || []) {
      const tid = String(o.achievement_type_id || "");
      if (!tid) continue;
      if (!m[tid]) m[tid] = [];
      m[tid].push(o);
    }
    for (const k of Object.keys(m)) {
      m[k].sort((a, b) => {
        const sa = asInt(a.sort, 0), sb = asInt(b.sort, 0);
        if (sa !== sb) return sa - sb;
        return String(a.label || "").localeCompare(String(b.label || ""));
      });
    }
    return m;
  }, [options]);

  const canAdmin = useMemo(() => !!(isAppAdmin || isDashboardOwner), [isAppAdmin, isDashboardOwner]);

  function reqRequired(r: AnyRow) {
    const t = typeById[String(r.achievement_type_id)] || {};
    return Math.max(1, asInt(r.required_count ?? t.required_count, 1));
  }
  function reqCurrent(r: AnyRow) {
    return Math.max(0, asInt(r.current_count, 0));
  }

  async function loadAll() {
    setLoading(true);
    setMsg(null);

    const u = await supabase.auth.getUser();
    const uid = u.data.user?.id || null;
    setUserId(uid);

    const a = await safeRpcBool("is_app_admin");
    const o = await safeRpcBool("is_dashboard_owner");
    setIsAppAdmin(a);
    setIsDashboardOwner(o);

    const t = await supabase
      .from("state_achievement_types")
      .select("*")
      .eq("state_code", stateCode)
      .order("name", { ascending: true });

    if (t.error) {
      setTypes([]); setOptions([]); setRequests([]); setAccess([]);
      setMsg("Types load failed: " + t.error.message);
      setLoading(false);
      return;
    }
    const tData = (t.data as any[]) || [];
    setTypes(tData);

    const ids = tData.map((x) => x?.id).filter(Boolean);
    if (ids.length) {
      const op = await supabase
        .from("state_achievement_options")
        .select("*")
        .in("achievement_type_id", ids)
        .order("sort", { ascending: true })
        .order("label", { ascending: true });
      if (!op.error) setOptions((op.data as any[]) || []);
      else setOptions([]);
    } else {
      setOptions([]);
    }

    const r = await supabase
      .from("state_achievement_requests")
      .select("*")
      .eq("state_code", stateCode)
      .order("created_at", { ascending: false })
      .limit(800);

    if (!r.error) setRequests((r.data as any[]) || []);
    else {
      setRequests([]);
      setMsg((p) => (p ? p + " | " : "") + "Requests load failed: " + r.error.message);
    }

    const ac = await supabase
      .from("state_achievement_access")
      .select("*")
      .eq("state_code", stateCode)
      .order("created_at", { ascending: false })
      .limit(300);

    if (!ac.error) setAccess((ac.data as any[]) || []);
    else {
      setAccess([]);
      setMsg((p) => (p ? p + " | " : "") + "Access load failed: " + ac.error.message);
    }

    setLoading(false);
  }

  useEffect(() => { loadAll(); }, [stateCode]);

  function setLocalRequests(id: any, patch: AnyRow) {
    setRequests((prev) => prev.map((x) => (String(x.id) === String(id) ? { ...x, ...patch } : x)));
  }

  async function safeUpdateRequest(id: string, patch: AnyRow) {
    // Retry without completed_at if column doesn't exist
    const attempt = async (p: AnyRow) => supabase.from("state_achievement_requests").update(p as any).eq("id", id);

    let res = await attempt(patch);
    if (!res.error) return res;

    const m = (res.error.message || "").toLowerCase();
    if (m.includes("completed_at") && Object.prototype.hasOwnProperty.call(patch, "completed_at")) {
      const p2 = { ...patch };
      delete p2.completed_at;
      res = await attempt(p2);
      return res;
    }
    return res;
  }

  function toggleSelectedRequestId(id: string) {
    const key = String(id || "");
    if (!key) return;
    setSelectedRequestIds((prev) => prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]);
  }

  async function loadBulkDiscordWebhooks(allianceCode: string) {
    try {
      const a = String(allianceCode || "").trim().toUpperCase();
      if (!a) {
        setBulkDiscordWebhooks([]);
        setBulkDiscordWebhookId("");
        return;
      }

      const r = await supabase
        .from("alliance_discord_webhooks")
        .select("id, alliance_code, label, active")
        .eq("alliance_code", a)
        .eq("active", true)
        .order("label", { ascending: true });

      if (r.error) throw r.error;

      const rows = (r.data || []) as AnyRow[];
      setBulkDiscordWebhooks(rows);

      if (!rows.some((x) => String(x?.id || "") === String(bulkDiscordWebhookId || ""))) {
        setBulkDiscordWebhookId(rows.length ? String(rows[0]?.id || "") : "");
      }
    } catch {
      setBulkDiscordWebhooks([]);
      setBulkDiscordWebhookId("");
    }
  }

  function clearSelectedRequests() {
    setSelectedRequestIds([]);
  }

  useEffect(() => {
    void loadBulkDiscordWebhooks(bulkDiscordAlliance);
  }, [bulkDiscordAlliance]);

  useEffect(() => {
    setBulkDiscordPresets(loadBulkDiscordPresets());
  }, []);

  function selectAllVisibleRequests() {
    const ids = filteredRequests.slice(0, 200).map((r) => String(r?.id || "")).filter(Boolean);
    setSelectedRequestIds(ids);
  }

  function selectRequestsByStatus(status: string) {
    const ids = filteredRequests
      .slice(0, 200)
      .filter((r) => String(r?.status || "").toLowerCase() === String(status || "").toLowerCase())
      .map((r) => String(r?.id || ""))
      .filter(Boolean);

    setSelectedRequestIds(ids);
  }

  async function bulkSetRequestStatus(nextStatus: string) {
    try {
      const ids = selectedRequestIds.slice();
      if (!ids.length) {
        setMsg("Select at least one request first.");
        return;
      }

      if (!window.confirm(`Apply "${nextStatus}" to ${ids.length} selected requests?`)) return;

      setBulkBusy(true);
      setMsg(`Updating ${ids.length} requests...`);

      for (const id of ids) {
        const row = requests.find((x) => String(x?.id || "") === String(id));
        if (!row) continue;

        const req = reqRequired(row);
        const patch: AnyRow = {
          status: nextStatus,
        };

        if (String(nextStatus).toLowerCase() === "completed") {
          patch.current_count = req;
          patch.completed_at = nowUtc();
        }

        await safeUpdateRequest(String(id), patch);
      }

      setSelectedRequestIds([]);
      setMsg(`Bulk update complete ✅ (${ids.length})`);
      await loadAll();
    } catch (e: any) {
      setMsg("Bulk update failed: " + String(e?.message || e || "unknown error"));
    } finally {
      setBulkBusy(false);
    }
  }

  function formatBulkDiscordAchievementLine(r: AnyRow) {
    const player = String(r?.player_name || "Player");
    const alliance = String(r?.alliance_name || r?.alliance_code || "—");
    const type = String(typeName(r?.achievement_type_id) || "Achievement");
    const option = r?.option_id ? String(optionLabel(r?.option_id) || "") : "";
    const status = String(r?.status || "submitted");
    const req = reqRequired(r);
    const cur = reqCurrent(r);

    return `• ${player} — ${type}${option ? ` — ${option}` : ""} (${cur}/${req}, ${status}, ${alliance})`;
  }

  function saveCurrentBulkDiscordTemplate() {
    const name = String(newBulkDiscordTemplateName || "").trim();
    if (!name) {
      setMsg("Enter a bulk Discord template name first.");
      return;
    }

    const template = {
      name,
      bulkDiscordMessageStyle,
      bulkDiscordTargetMode,
      bulkIncludeSubmitted,
      bulkIncludeInProgress,
      bulkIncludeCompleted,
    };

    const next = [
      ...bulkDiscordTemplates.filter((x) => String(x?.name || "").trim().toLowerCase() !== name.toLowerCase()),
      template,
    ].sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")));

    setBulkDiscordTemplates(next);
    saveBulkDiscordTemplates(next);
    setSelectedBulkDiscordTemplate(name);
    setNewBulkDiscordTemplateName("");
    setMsg("Bulk Discord template saved ✅");
  }

  function applySelectedBulkDiscordTemplate() {
    const name = String(selectedBulkDiscordTemplate || "").trim();
    const template = bulkDiscordTemplates.find((x) => String(x?.name || "").trim() === name);
    if (!template) {
      setMsg("Pick a bulk Discord template first.");
      return;
    }

    setBulkDiscordMessageStyle(String(template?.bulkDiscordMessageStyle || "detailed"));
    setBulkDiscordTargetMode(String(template?.bulkDiscordTargetMode || "grouped"));
    setBulkIncludeSubmitted(Boolean(template?.bulkIncludeSubmitted ?? true));
    setBulkIncludeInProgress(Boolean(template?.bulkIncludeInProgress ?? true));
    setBulkIncludeCompleted(Boolean(template?.bulkIncludeCompleted ?? true));
    setMsg("Bulk Discord template loaded ✅");
  }

  function deleteSelectedBulkDiscordTemplate() {
    const name = String(selectedBulkDiscordTemplate || "").trim();
    if (!name) {
      setMsg("Pick a bulk Discord template first.");
      return;
    }

    const next = bulkDiscordTemplates.filter((x) => String(x?.name || "").trim() !== name);
    setBulkDiscordTemplates(next);
    saveBulkDiscordTemplates(next);
    setSelectedBulkDiscordTemplate("");
    setMsg("Bulk Discord template deleted ✅");
  }

  function saveCurrentBulkDiscordPreset() {
    const name = String(newBulkDiscordPresetName || "").trim();
    if (!name) {
      setMsg("Enter a bulk Discord preset name first.");
      return;
    }

    const preset = {
      name,
      bulkDiscordAlliance,
      bulkDiscordWebhookId,
      bulkDiscordMessageStyle,
      bulkDiscordTargetMode,
      bulkIncludeSubmitted,
      bulkIncludeInProgress,
      bulkIncludeCompleted,
    };

    const next = [
      ...bulkDiscordPresets.filter((x) => String(x?.name || "").trim().toLowerCase() !== name.toLowerCase()),
      preset,
    ].sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")));

    setBulkDiscordPresets(next);
    saveBulkDiscordPresets(next);
    setSelectedBulkDiscordPreset(name);
    setNewBulkDiscordPresetName("");
    setMsg("Bulk Discord preset saved ✅");
  }

  function applySelectedBulkDiscordPreset() {
    const name = String(selectedBulkDiscordPreset || "").trim();
    const preset = bulkDiscordPresets.find((x) => String(x?.name || "").trim() === name);
    if (!preset) {
      setMsg("Pick a bulk Discord preset first.");
      return;
    }

    setBulkDiscordAlliance(String(preset?.bulkDiscordAlliance || "WOC"));
    setBulkDiscordWebhookId(String(preset?.bulkDiscordWebhookId || ""));
    setBulkDiscordMessageStyle(String(preset?.bulkDiscordMessageStyle || "detailed"));
    setBulkDiscordTargetMode(String(preset?.bulkDiscordTargetMode || "grouped"));
    setBulkIncludeSubmitted(Boolean(preset?.bulkIncludeSubmitted ?? true));
    setBulkIncludeInProgress(Boolean(preset?.bulkIncludeInProgress ?? true));
    setBulkIncludeCompleted(Boolean(preset?.bulkIncludeCompleted ?? true));
    setMsg("Bulk Discord preset loaded ✅");
  }

  function deleteSelectedBulkDiscordPreset() {
    const name = String(selectedBulkDiscordPreset || "").trim();
    if (!name) {
      setMsg("Pick a bulk Discord preset first.");
      return;
    }

    const next = bulkDiscordPresets.filter((x) => String(x?.name || "").trim() !== name);
    setBulkDiscordPresets(next);
    saveBulkDiscordPresets(next);
    setSelectedBulkDiscordPreset("");
    setMsg("Bulk Discord preset deleted ✅");
  }

  async function previewBulkSendSelectedToDiscord() {
    try {
      const ids = selectedRequestIds.slice();
      if (!ids.length) {
        setMsg("Select at least one request first.");
        return;
      }

      const selected = requests.filter((r) => ids.includes(String(r?.id || "")));
      if (!selected.length) {
        setMsg("No selected requests found.");
        return;
      }

      const alliance = String(bulkDiscordAlliance || "WOC").trim().toUpperCase();
      const submitted = selected.filter((r) => String(r?.status || "").toLowerCase() === "submitted");
      const inProgress = selected.filter((r) => String(r?.status || "").toLowerCase() === "in_progress");
      const completed = selected.filter((r) => String(r?.status || "").toLowerCase() === "completed");

      const parts: string[] = [
        `🩸 **State ${stateCode} — Selected Achievements**`,
        `Alliance: **${alliance}**`,
        `Style: **${bulkDiscordMessageStyle}**`,
        `Selected Rows: **${selected.length}** • Submitted: **${submitted.length}** • In Progress: **${inProgress.length}** • Completed: **${completed.length}**`,
      ];

      const completedLines = completed.map((r) => formatBulkDiscordAchievementLine(r));
      const inProgressLines = inProgress.map((r) => formatBulkDiscordAchievementLine(r));
      const submittedLines = submitted.map((r) => formatBulkDiscordAchievementLine(r));

      if (bulkDiscordMessageStyle === "brief") {
        if (bulkIncludeCompleted && completed.length) parts.push("", "✅ **Completed**", ...completed.slice(0, 5).map((r) => `• ${String(r?.player_name || "Player")}`));
        if (bulkIncludeInProgress && inProgress.length) parts.push("", "🧬 **In Progress**", ...inProgress.slice(0, 5).map((r) => `• ${String(r?.player_name || "Player")}`));
        if (bulkIncludeSubmitted && submitted.length) parts.push("", "⏳ **Submitted**", ...submitted.slice(0, 5).map((r) => `• ${String(r?.player_name || "Player")}`));
      } else if (bulkDiscordMessageStyle === "compact") {
        if (bulkIncludeCompleted && completed.length) parts.push("", "✅ **Completed**", ...completedLines.slice(0, 5));
        if (bulkIncludeInProgress && inProgress.length) parts.push("", "🧬 **In Progress**", ...inProgressLines.slice(0, 5));
        if (bulkIncludeSubmitted && submitted.length) parts.push("", "⏳ **Submitted**", ...submittedLines.slice(0, 5));
      } else {
        if (bulkIncludeCompleted && completed.length) parts.push("", "✅ **Completed**", ...completedLines.slice(0, 10));
        if (bulkIncludeInProgress && inProgress.length) parts.push("", "🧬 **In Progress**", ...inProgressLines.slice(0, 10));
        if (bulkIncludeSubmitted && submitted.length) parts.push("", "⏳ **Submitted**", ...submittedLines.slice(0, 10));
      }

      setBulkDiscordPreview(parts.join("\n"));
      setMsg("Preview ready ✅");
    } catch (e: any) {
      setMsg("Preview failed: " + String(e?.message || e || "unknown error"));
    }
  }

  async function bulkSendSelectedToDiscord() {
    try {
      const ids = selectedRequestIds.slice();
      if (!ids.length) {
        setMsg("Select at least one request first.");
        return;
      }

      const selected = requests.filter((r) => ids.includes(String(r?.id || "")));
      if (!selected.length) {
        setMsg("No selected requests found.");
        return;
      }

      if (!bulkDiscordPreview.trim()) {
        await previewBulkSendSelectedToDiscord();
        return;
      }

      const grouped: Record<string, AnyRow[]> = {};
      for (const r of selected) {
        const rowAlliance = String(r?.alliance_code || r?.alliance_name || bulkDiscordAlliance || "WOC").trim().toUpperCase();
        const alliance = bulkDiscordTargetMode === "single" ? String(bulkDiscordAlliance || rowAlliance).trim().toUpperCase() : rowAlliance;
        if (!grouped[alliance]) grouped[alliance] = [];
        grouped[alliance].push(r);
      }

      if (!window.confirm(`Queue Discord sends for ${selected.length} selected rows across ${Object.keys(grouped).length} target group(s)?`)) return;

      setBulkBusy(true);
      setMsg("Queueing grouped Discord sends...");

      for (const alliance of Object.keys(grouped)) {
        const rows = grouped[alliance];
        const submitted = rows.filter((r) => String(r?.status || "").toLowerCase() === "submitted");
        const inProgress = rows.filter((r) => String(r?.status || "").toLowerCase() === "in_progress");
        const completed = rows.filter((r) => String(r?.status || "").toLowerCase() === "completed");

        const parts: string[] = [
          `🩸 **State ${stateCode} — Selected Achievements**`,
          `Alliance: **${alliance}**`,
          `Style: **${bulkDiscordMessageStyle}**`,
          `Selected Rows: **${rows.length}** • Submitted: **${submitted.length}** • In Progress: **${inProgress.length}** • Completed: **${completed.length}**`,
        ];

        const completedLines = completed.map((r) => formatBulkDiscordAchievementLine(r));
        const inProgressLines = inProgress.map((r) => formatBulkDiscordAchievementLine(r));
        const submittedLines = submitted.map((r) => formatBulkDiscordAchievementLine(r));

        if (bulkDiscordMessageStyle === "brief") {
          if (bulkIncludeCompleted && completed.length) parts.push("", "✅ **Completed**", ...completed.slice(0, 5).map((r) => `• ${String(r?.player_name || "Player")}`));
          if (bulkIncludeInProgress && inProgress.length) parts.push("", "🧬 **In Progress**", ...inProgress.slice(0, 5).map((r) => `• ${String(r?.player_name || "Player")}`));
          if (bulkIncludeSubmitted && submitted.length) parts.push("", "⏳ **Submitted**", ...submitted.slice(0, 5).map((r) => `• ${String(r?.player_name || "Player")}`));
        } else if (bulkDiscordMessageStyle === "compact") {
          if (bulkIncludeCompleted && completed.length) parts.push("", "✅ **Completed**", ...completedLines.slice(0, 5));
          if (bulkIncludeInProgress && inProgress.length) parts.push("", "🧬 **In Progress**", ...inProgressLines.slice(0, 5));
          if (bulkIncludeSubmitted && submitted.length) parts.push("", "⏳ **Submitted**", ...submittedLines.slice(0, 5));
        } else {
          if (bulkIncludeCompleted && completed.length) parts.push("", "✅ **Completed**", ...completedLines.slice(0, 10));
          if (bulkIncludeInProgress && inProgress.length) parts.push("", "🧬 **In Progress**", ...inProgressLines.slice(0, 10));
          if (bulkIncludeSubmitted && submitted.length) parts.push("", "⏳ **Submitted**", ...submittedLines.slice(0, 10));
        }

        const msg = parts.join("\n");

        const q = await supabase.rpc("queue_discord_send" as any, {
          p_kind: "discord_webhook",
          p_target: "alliance:" + alliance,
          p_channel_id: "default:achievements",
          p_content: msg,
          p_meta: {
            kind: "achievements_bulk_selected_grouped",
            source: "OwnerStateAchievementsPage",
            state_code: stateCode,
            alliance_code: alliance,
            selected_ids: rows.map((r) => String(r?.id || "")),
          },
        });

        if (q.error) throw q.error;
      }

      setMsg(`Grouped Discord sends queued ✅ (${selected.length} rows, ${Object.keys(grouped).length} alliance group(s))`);
      setBulkDiscordPreview("");
    } catch (e: any) {
      setMsg("Bulk Discord send failed: " + String(e?.message || e || "unknown error"));
    } finally {
      setBulkBusy(false);
    }
  }

  async function saveRequestRow(r: AnyRow) {
    if (!canAdmin) return;
    setMsg(null);

    const id = String(r.id || "");
    if (!id) return;

    const req = reqRequired(r);
    const cur = reqCurrent(r);
    const status = String(r.status || "submitted");
    const done = (status === "completed") || (cur >= req);

    const patch: AnyRow = {
      current_count: cur,
      status: done ? "completed" : status,
      notes: r.notes ?? null
    };
    if (done && (r.completed_at == null)) patch.completed_at = nowUtc();

    const u = await safeUpdateRequest(id, patch);
    if (u.error) {
      setMsg("Update failed: " + u.error.message);
      return;
    }
    setMsg("✅ Request updated.");
    await loadAll();
  }

  // ---- Types CRUD ----
  const [editType, setEditType] = useState<AnyRow | null>(null);
  const [newTypeName, setNewTypeName] = useState("");
  const [newTypeKind, setNewTypeKind] = useState("count");
  const [newTypeReqCount, setNewTypeReqCount] = useState("1");
  const [newTypeRequiresOption, setNewTypeRequiresOption] = useState(false);

  async function createType() {
    if (!canAdmin) return;
    setMsg(null);

    const name = norm(newTypeName);
    if (!name) return setMsg("Type name required.");

    const payload: AnyRow = {
      state_code: stateCode,
      name,
      kind: norm(newTypeKind) || "count",
      requires_option: !!newTypeRequiresOption,
      required_count: Math.max(1, asInt(newTypeReqCount, 1)),
      active: true
    };

    const ins = await supabase.from("state_achievement_types").insert(payload as any).select("*").maybeSingle();
    if (ins.error) return setMsg("Create type failed: " + ins.error.message);

    setNewTypeName("");
    setMsg("✅ Type created.");
    await loadAll();
  }

  async function saveType(t: AnyRow) {
    if (!canAdmin) return;
    setMsg(null);

    const id = String(t.id || "");
    if (!id) return;

    const patch: AnyRow = {
      name: norm(t.name),
      kind: norm(t.kind) || "count",
      requires_option: t.requires_option === true,
      required_count: Math.max(1, asInt(t.required_count, 1)),
      active: t.active === true
    };

    const up = await supabase.from("state_achievement_types").update(patch as any).eq("id", id);
    if (up.error) return setMsg("Update type failed: " + up.error.message);

    setMsg("✅ Type updated.");
    await loadAll();
  }

  async function disableType(id: string) {
    if (!canAdmin) return;
    if (!window.confirm("Disable this type? (safer than delete)")) return;
    const up = await supabase.from("state_achievement_types").update({ active: false } as any).eq("id", id);
    if (up.error) return setMsg("Disable failed: " + up.error.message);
    setMsg("✅ Disabled.");
    await loadAll();
  }

  // ---- Options CRUD ----
  const [selectedTypeId, setSelectedTypeId] = useState<string>("");
  const [newOptLabel, setNewOptLabel] = useState("");
  const [newOptSort, setNewOptSort] = useState("0");

  useEffect(() => {
    if (!selectedTypeId && types.length) setSelectedTypeId(String(types[0].id));
  }, [types, selectedTypeId]);

  async function createOption() {
    if (!canAdmin) return;
    setMsg(null);

    const tid = String(selectedTypeId || "");
    if (!tid) return setMsg("Pick a type first.");

    const label = norm(newOptLabel).replace(/^#/, "");
    if (!label) return setMsg("Option label required (ex: Rail Gun).");

    const payload: AnyRow = {
      achievement_type_id: tid,
      label,
      sort: asInt(newOptSort, 0),
      active: true
    };

    const ins = await supabase.from("state_achievement_options").insert(payload as any).select("*").maybeSingle();
    if (ins.error) return setMsg("Create option failed: " + ins.error.message);

    setNewOptLabel("");
    setMsg("✅ Option created.");
    await loadAll();
  }

  async function saveOption(o: AnyRow) {
    if (!canAdmin) return;
    setMsg(null);

    const id = String(o.id || "");
    if (!id) return;

    const patch: AnyRow = {
      label: norm(o.label),
      sort: asInt(o.sort, 0),
      active: o.active === true
    };

    const up = await supabase.from("state_achievement_options").update(patch as any).eq("id", id);
    if (up.error) return setMsg("Update option failed: " + up.error.message);

    setMsg("✅ Option updated.");
    await loadAll();
  }

  async function disableOption(id: string) {
    if (!canAdmin) return;
    if (!window.confirm("Disable this option? (safer than delete)")) return;
    const up = await supabase.from("state_achievement_options").update({ active: false } as any).eq("id", id);
    if (up.error) return setMsg("Disable failed: " + up.error.message);
    setMsg("✅ Disabled.");
    await loadAll();
  }

  // ---- Access CRUD ----
  const [newAccessUserId, setNewAccessUserId] = useState("");
  const [newAccessCanView, setNewAccessCanView] = useState(true);
  const [newAccessCanEdit, setNewAccessCanEdit] = useState(false);

  async function upsertAccess() {
    if (!canAdmin) return;
    setMsg(null);

    const uid = norm(newAccessUserId);
    if (!uid) return setMsg("User ID required.");

    const payload: AnyRow = {
      state_code: stateCode,
      user_id: uid,
      can_view: !!newAccessCanView,
      can_edit: !!newAccessCanEdit
    };

    const ins = await supabase.from("state_achievement_access").upsert(payload as any, { onConflict: "state_code,user_id" } as any).select("*").maybeSingle();
    if (ins.error) return setMsg("Access upsert failed: " + ins.error.message);

    setNewAccessUserId("");
    setMsg("✅ Access saved.");
    await loadAll();
  }

  async function saveAccessRow(a: AnyRow) {
    if (!canAdmin) return;
    setMsg(null);

    const id = a.id ? String(a.id) : null;
    if (id) {
      const patch: AnyRow = { can_view: a.can_view === true, can_edit: a.can_edit === true };
      const up = await supabase.from("state_achievement_access").update(patch as any).eq("id", id);
      if (up.error) return setMsg("Access update failed: " + up.error.message);
      setMsg("✅ Access updated.");
      await loadAll();
      return;
    }

    // fallback if table uses composite keys and no id
    const uid = String(a.user_id || "");
    if (!uid) return setMsg("Access row missing user_id.");
    const payload: AnyRow = { state_code: stateCode, user_id: uid, can_view: a.can_view === true, can_edit: a.can_edit === true };
    const up2 = await supabase.from("state_achievement_access").upsert(payload as any, { onConflict: "state_code,user_id" } as any);
    if (up2.error) return setMsg("Access save failed: " + up2.error.message);
    setMsg("✅ Access saved.");
    await loadAll();
  }

  async function removeAccess(a: AnyRow) {
    if (!canAdmin) return;
    if (!window.confirm("Remove access row?")) return;

    setMsg(null);
    if (a.id) {
      const del = await supabase.from("state_achievement_access").delete().eq("id", String(a.id));
      if (del.error) return setMsg("Delete failed: " + del.error.message);
      setMsg("✅ Removed.");
      await loadAll();
      return;
    }

    const uid = String(a.user_id || "");
    if (!uid) return setMsg("Row missing user_id.");
    const del2 = await supabase.from("state_achievement_access").delete().eq("state_code", stateCode).eq("user_id", uid);
    if (del2.error) return setMsg("Delete failed: " + del2.error.message);
    setMsg("✅ Removed.");
    await loadAll();
  }

  // ---- Export/Import ----
  async function copyJson(obj: any, label: string) {
    const txt = JSON.stringify(obj, null, 2);
    try { await navigator.clipboard.writeText(txt); setMsg("✅ Copied " + label + " JSON."); }
    catch { window.prompt("Copy " + label + " JSON:", txt); }
  }

  async function seedDefaults() {
    if (!canAdmin) return;
    setMsg(null);

    // SWP Weapon type
    const existingSwp = (types || []).find((t) => normLower(t.name) === "swp weapon");
    let swpId: string | null = existingSwp?.id ? String(existingSwp.id) : null;

    if (!swpId) {
      const ins = await supabase.from("state_achievement_types").insert({
        state_code: stateCode,
        name: "SWP Weapon",
        kind: "count",
        requires_option: true,
        required_count: 1,
        active: true
      } as any).select("*").maybeSingle();

      if (ins.error) return setMsg("Seed SWP type failed: " + ins.error.message);
      swpId = ins.data?.id ? String(ins.data.id) : null;
    }

    // Governor Rotations type
    const existingGov = (types || []).find((t) => normLower(t.name) === "governor rotations");
    if (!existingGov) {
      const ins2 = await supabase.from("state_achievement_types").insert({
        state_code: stateCode,
        name: "Governor Rotations",
        kind: "count",
        requires_option: false,
        required_count: 3,
        active: true
      } as any).select("*").maybeSingle();

      if (ins2.error) return setMsg("Seed Governor type failed: " + ins2.error.message);
    }

    // Rail Gun option
    if (swpId) {
      const existsRail = (options || []).some((o) => String(o.achievement_type_id) === swpId && normLower(o.label) === "rail gun");
      if (!existsRail) {
        const ins3 = await supabase.from("state_achievement_options").insert({
          achievement_type_id: swpId,
          label: "Rail Gun",
          sort: 0,
          active: true
        } as any).select("*").maybeSingle();

        if (ins3.error) return setMsg("Seed Rail Gun failed: " + ins3.error.message);
      }
    }

    setMsg("✅ Seeded defaults (SWP Weapon, Governor Rotations, Rail Gun).");
    await loadAll();
  }

  // UI helpers
  function typeName(id: any) {
    const t = id ? typeById[String(id)] : null;
    return String(t?.name || "Achievement");
  }
  function optionLabel(id: any) {
    if (!id) return "";
    const o = options.find((x) => String(x.id) === String(id));
    return o ? String(o.label || "") : "";
  }
  async function deleteAchievementRequest(id: string) {
    const rowId = String(id ?? "").trim();
    if (!rowId) return;
    if (!window.confirm("Delete this achievement request?")) return;

    const { error } = await supabase
      .from("state_achievement_requests")
      .delete()
      .eq("id", rowId);

    if (error) {
      window.alert("Delete failed: " + error.message);
      return;
    }

    await loadAll();
  }

  async function deleteAchievementRequestsForPlayer(playerName: string) {
    const name = String(playerName ?? "").trim();
    if (!name) return;
    if (!window.confirm(`Delete ALL achievement requests for ${name}?`)) return;

    const { error } = await supabase
      .from("state_achievement_requests")
      .delete()
      .eq("state_code", stateCode)
      .eq("player_name", name);

    if (error) {
      window.alert("Delete-all failed: " + error.message);
      return;
    }

    await loadAll();
  }


  return (
    <div className="owner-state-achievements-page owner-state-achievements-page__btnfix" style={{ padding: 16, maxWidth: 1400, margin: "0 auto", display: "grid", gap: 12 }}>
<style>{`
  .owner-state-achievements-page__btnfix button,
  .owner-state-achievements-page__btnfix a[role="button"],
  .owner-state-achievements-page__btnfix .zombie-btn,
  .owner-state-achievements-page__btnfix .zombie-btn:link,
  .owner-state-achievements-page__btnfix .zombie-btn:visited {
    color: #111111 !important;
    background: #e8e8e8 !important;
    border: 1px solid #bdbdbd !important;
    text-shadow: none !important;
    opacity: 1 !important;
    font-weight: 800 !important;
  }

  .owner-state-achievements-page__btnfix button:hover:not(:disabled),
  .owner-state-achievements-page__btnfix a[role="button"]:hover,
  .owner-state-achievements-page__btnfix .zombie-btn:hover:not(:disabled) {
    color: #000000 !important;
    background: #ffffff !important;
    border-color: #9f9f9f !important;
  }

  .owner-state-achievements-page__btnfix button:disabled,
  .owner-state-achievements-page__btnfix .zombie-btn:disabled {
    color: #555555 !important;
    background: #dcdcdc !important;
    border-color: #bbbbbb !important;
    text-shadow: none !important;
    opacity: 1 !important;
  }
`}</style>
      <div
        className="zombie-card"
        style={{
          padding: 18,
          background: "linear-gradient(180deg, rgba(16,20,26,0.98), rgba(8,10,14,0.94))",
          border: "1px solid rgba(255,255,255,0.10)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div style={{ minWidth: 280 }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              <div style={{ padding: "6px 10px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", fontSize: 12, fontWeight: 800 }}>STATE ACHIEVEMENTS</div>
              <div style={{ padding: "6px 10px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", fontSize: 12, fontWeight: 800 }}>OWNER ADMIN</div>
              <div style={{ padding: "6px 10px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)", fontSize: 12, fontWeight: 800 }}>STATE {stateCode}</div>
            </div>

            <h2 style={{ margin: 0, fontSize: 30, lineHeight: 1.05 }}>Owner State Achievements Hub</h2>
            <div style={{ opacity: 0.82, fontSize: 14, marginTop: 10, lineHeight: 1.6, maxWidth: 860 }}>
              Manage requests, achievement types, options, helper access, and export tools from one owner workspace.
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <button className="zombie-btn" type="button" style={{ padding: "10px 12px" }} onClick={() => window.location.assign("/owner")}>Back to Owner</button>
            <button className="zombie-btn" type="button" style={{ padding: "10px 12px" }} onClick={() => window.location.assign("/state/789/achievements-form")}>Player Form</button>
            <button className="zombie-btn" type="button" style={{ padding: "10px 12px" }} onClick={() => window.location.assign("/state/789/achievements-progress")}>Progress</button>
            <button className="zombie-btn" type="button" style={{ padding: "10px 12px" }} onClick={() => window.location.assign("/state/789/achievements-tracker")}>Tracker</button>
            <button className="zombie-btn" type="button" style={{ padding: "10px 12px" }} onClick={loadAll}>Refresh</button>
            <SupportBundleButton />
          </div>
        </div>
      </div>

      <div
        className="zombie-card"
        style={{
          padding: 14,
          background: "rgba(0,0,0,0.20)",
        }}
      >
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ opacity: 0.78, fontSize: 12, fontWeight: 700 }}>State Code</div>
          <input className="zombie-input" value={stateCode} onChange={(e) => setStateCode(e.target.value)} style={{ padding: "10px 12px", width: 120 }} />
          <div style={{ marginLeft: "auto", opacity: 0.75, fontSize: 12 }}>
            user={userId ? "yes" : "no"} • admin={String(isAppAdmin)} • owner={String(isDashboardOwner)}
          </div>
        </div>

        <div style={{ opacity: 0.78, fontSize: 12, marginTop: 8 }}>
          {loading ? "Loading…" : `Types: ${types.length} • Options: ${options.length} • Requests: ${requests.length} • Access: ${access.length}`}
        </div>

        {(initialAllianceFromQuery || initialTypeFromQuery || initialPlayerFromQuery || initialStatusFromQuery) ? (
          <div
            style={{
              marginTop: 10,
              border: "1px solid rgba(120,180,255,0.28)",
              background: "rgba(120,180,255,0.08)",
              borderRadius: 12,
              padding: 10,
            }}
          >
            <div style={{ fontWeight: 800 }}>Deep link filters active</div>
            <div style={{ fontSize: 12, opacity: 0.82, marginTop: 4 }}>
              {initialAllianceFromQuery ? `Alliance: ${initialAllianceFromQuery}` : ""}
              {initialTypeFromQuery ? `${initialAllianceFromQuery ? " • " : ""}Type: ${initialTypeFromQuery}` : ""}
              {initialPlayerFromQuery ? `${(initialAllianceFromQuery || initialTypeFromQuery) ? " • " : ""}Player: ${initialPlayerFromQuery}` : ""}
              {initialStatusFromQuery ? `${(initialAllianceFromQuery || initialTypeFromQuery || initialPlayerFromQuery) ? " • " : ""}Status: ${initialStatusFromQuery}` : ""}
            </div>
          </div>
        ) : null}

        {msg ? (
          <div
            style={{
              marginTop: 10,
              border:
                String(msg || "").includes("✅")
                  ? "1px solid rgba(120,255,120,0.35)"
                  : /failed|error|missing|load/i.test(String(msg || ""))
                  ? "1px solid rgba(255,120,120,0.35)"
                  : "1px solid rgba(255,255,255,0.14)",
              background:
                String(msg || "").includes("✅")
                  ? "rgba(120,255,120,0.08)"
                  : /failed|error|missing|load/i.test(String(msg || ""))
                  ? "rgba(255,120,120,0.08)"
                  : "rgba(255,255,255,0.04)",
              borderRadius: 10,
              padding: 10,
              fontWeight: String(msg || "").includes("✅") ? 700 : 500,
            }}
          >
            {msg}
          </div>
        ) : null}
      </div>

      <div
        className="zombie-card"
        style={{
          padding: 14,
          background: "rgba(0,0,0,0.22)",
        }}
      >
        <div style={{ fontWeight: 900, fontSize: 16, marginBottom: 8 }}>Exports and Discord tools</div>
        <div style={{ opacity: 0.72, fontSize: 12, marginBottom: 12 }}>
          Export reports, filter by deep-link values, and prepare achievement output for Discord without leaving this page.
        </div>

        <StateAchievementsExportPanel stateCode={stateCode} requests={requests} types={types} options={options} initialAllianceFilter={initialAllianceFromQuery || "ALL"} initialTypeFilter={initialTypeFromQuery || "ALL"} />
      </div>

      <div className="zombie-card" style={{ marginTop: 0 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            className="zombie-btn"
            style={{
              padding: "10px 12px",
              fontWeight: tab==="requests" ? 900 : 600,
              border: tab==="requests" ? "1px solid rgba(255,255,255,0.22)" : "1px solid rgba(255,255,255,0.10)",
              background: tab==="requests" ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.03)"
            }}
            onClick={() => setTab("requests")}
          >
            Requests
          </button>
          <button
            className="zombie-btn"
            style={{
              padding: "10px 12px",
              fontWeight: tab==="types" ? 900 : 600,
              border: tab==="types" ? "1px solid rgba(255,255,255,0.22)" : "1px solid rgba(255,255,255,0.10)",
              background: tab==="types" ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.03)"
            }}
            onClick={() => setTab("types")}
          >
            Types
          </button>
          <button
            className="zombie-btn"
            style={{
              padding: "10px 12px",
              fontWeight: tab==="options" ? 900 : 600,
              border: tab==="options" ? "1px solid rgba(255,255,255,0.22)" : "1px solid rgba(255,255,255,0.10)",
              background: tab==="options" ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.03)"
            }}
            onClick={() => setTab("options")}
          >
            Options
          </button>
          <button
            className="zombie-btn"
            style={{
              padding: "10px 12px",
              fontWeight: tab==="access" ? 900 : 600,
              border: tab==="access" ? "1px solid rgba(255,255,255,0.22)" : "1px solid rgba(255,255,255,0.10)",
              background: tab==="access" ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.03)"
            }}
            onClick={() => setTab("access")}
          >
            Access
          </button>
          <button
            className="zombie-btn"
            style={{
              padding: "10px 12px",
              fontWeight: tab==="export" ? 900 : 600,
              border: tab==="export" ? "1px solid rgba(255,255,255,0.22)" : "1px solid rgba(255,255,255,0.10)",
              background: tab==="export" ? "rgba(255,255,255,0.10)" : "rgba(255,255,255,0.03)"
            }}
            onClick={() => setTab("export")}
          >
            Export/Import
          </button>
          <div style={{ marginLeft: "auto" }}>
            <button className="zombie-btn" style={{ padding: "10px 12px" }} onClick={seedDefaults} disabled={!canAdmin}>Seed Defaults</button>
          </div>
        </div>
      </div>

      {tab === "requests" ? (
        <div className="zombie-card" style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 900 }}>Requests Queue ({filteredRequests.length})</div>
          <div style={{ opacity: 0.7, fontSize: 12, marginTop: 6 }}>
            Update counts, status, and notes. Auto marks ✅ at 100%. Filters and bulk tools apply to the visible request list below.
          </div>

          <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
              <div style={{ fontSize: 12, opacity: 0.78, lineHeight: 1.45 }}>
                Showing {Math.min(filteredRequests.length, 200)} of {filteredRequests.length} filtered request(s)
                {requestActiveFilterSummary.length ? ` • Active filters: ${requestActiveFilterSummary.join(" • ")}` : " • Active filters: none"}
              </div>

              <button
                className="zombie-btn"
                type="button"
                style={{
                  padding: "8px 10px",
                  fontSize: 12,
                  border: "1px solid rgba(255,255,255,0.14)",
                  background: "rgba(255,255,255,0.05)"
                }}
                onClick={() => {
                  setRequestAllianceFilter("ALL");
                  setRequestPlayerFilter("");
                  setRequestTypeFilter("ALL");
                  setRequestStatusFilter("ALL");
                  setSelectedRequestIds([]);
                }}
              >
                Reset Request Filters
              </button>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
                padding: 10,
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.10)",
                background: "rgba(255,255,255,0.03)"
              }}
            >
              <div style={{ fontSize: 12, opacity: 0.82 }}>
                Selected: {selectedRequestIds.length}
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  className="zombie-btn"
                  type="button"
                  style={{ padding: "8px 10px", fontSize: 12 }}
                  disabled={bulkBusy}
                  onClick={selectAllVisibleRequests}
                >
                  Select Visible
                </button>

                <button
                  className="zombie-btn"
                  type="button"
                  style={{ padding: "8px 10px", fontSize: 12 }}
                  disabled={bulkBusy || !selectedRequestIds.length}
                  onClick={clearSelectedRequests}
                >
                  Clear Selected
                </button>

                <button
                  className="zombie-btn"
                  type="button"
                  style={{ padding: "8px 10px", fontSize: 12 }}
                  disabled={bulkBusy}
                  onClick={() => selectRequestsByStatus("submitted")}
                >
                  Select Submitted
                </button>

                <button
                  className="zombie-btn"
                  type="button"
                  style={{ padding: "8px 10px", fontSize: 12 }}
                  disabled={bulkBusy}
                  onClick={() => selectRequestsByStatus("in_progress")}
                >
                  Select In Progress
                </button>

                <button
                  className="zombie-btn"
                  type="button"
                  style={{ padding: "8px 10px", fontSize: 12 }}
                  disabled={bulkBusy}
                  onClick={() => selectRequestsByStatus("completed")}
                >
                  Select Completed
                </button>

                <button
                  className="zombie-btn"
                  type="button"
                  style={{
                    padding: "8px 10px",
                    fontSize: 12,
                    border: "1px solid rgba(255,220,120,0.24)",
                    background: "rgba(255,220,120,0.08)"
                  }}
                  disabled={bulkBusy || !selectedRequestIds.length}
                  onClick={() => void bulkSetRequestStatus("submitted")}
                >
                  Mark Submitted
                </button>

                <button
                  className="zombie-btn"
                  type="button"
                  style={{
                    padding: "8px 10px",
                    fontSize: 12,
                    border: "1px solid rgba(120,180,255,0.24)",
                    background: "rgba(120,180,255,0.08)"
                  }}
                  disabled={bulkBusy || !selectedRequestIds.length}
                  onClick={() => void bulkSetRequestStatus("in_progress")}
                >
                  Mark In Progress
                </button>

                <button
                  className="zombie-btn"
                  type="button"
                  style={{
                    padding: "8px 10px",
                    fontSize: 12,
                    border: "1px solid rgba(120,255,120,0.24)",
                    background: "rgba(120,255,120,0.08)"
                  }}
                  disabled={bulkBusy || !selectedRequestIds.length}
                  onClick={() => void bulkSetRequestStatus("completed")}
                >
                  Mark Completed
                </button>
              </div>
            </div>

            {filteredRequests.slice(0, 200).map((r) => {
              const req = reqRequired(r);
              const cur = reqCurrent(r);
              const done = (String(r.status) === "completed") || (cur >= req);
  async function deleteAchievementRequest(id: string) {
    const rowId = String(id ?? "").trim();
    if (!rowId) return;
    if (!window.confirm("Delete this achievement request?")) return;

    const { error } = await supabase
      .from("state_achievement_requests")
      .delete()
      .eq("id", rowId);

    if (error) {
      window.alert("Delete failed: " + error.message);
      return;
    }

    await loadAll();
  }

  async function deleteAchievementRequestsForPlayer(playerName: string) {
    const name = String(playerName ?? "").trim();
    if (!name) return;
    if (!window.confirm(`Delete ALL achievement requests for ${name}?`)) return;

    const { error } = await supabase
      .from("state_achievement_requests")
      .delete()
      .eq("state_code", stateCode)
      .eq("player_name", name);

    if (error) {
      window.alert("Delete-all failed: " + error.message);
      return;
    }

    await loadAll();
  }

              return (
    <div className="owner-state-achievements-page owner-state-achievements-page__btnfix"
                  key={String(r.id)}
                  style={{
                    padding: 10,
                    borderRadius: 12,
                    border: done ? "1px solid rgba(120,255,120,0.22)" : "1px solid rgba(255,255,255,0.10)",
                    background: done ? "rgba(120,255,120,0.05)" : "rgba(0,0,0,0.20)"
                  }}
                >
<style>{`
  .owner-state-achievements-page__btnfix button,
  .owner-state-achievements-page__btnfix a[role="button"],
  .owner-state-achievements-page__btnfix .zombie-btn,
  .owner-state-achievements-page__btnfix .zombie-btn:link,
  .owner-state-achievements-page__btnfix .zombie-btn:visited {
    color: #111111 !important;
    background: #e8e8e8 !important;
    border: 1px solid #bdbdbd !important;
    text-shadow: none !important;
    opacity: 1 !important;
    font-weight: 800 !important;
  }

  .owner-state-achievements-page__btnfix button:hover:not(:disabled),
  .owner-state-achievements-page__btnfix a[role="button"]:hover,
  .owner-state-achievements-page__btnfix .zombie-btn:hover:not(:disabled) {
    color: #000000 !important;
    background: #ffffff !important;
    border-color: #9f9f9f !important;
  }

  .owner-state-achievements-page__btnfix button:disabled,
  .owner-state-achievements-page__btnfix .zombie-btn:disabled {
    color: #555555 !important;
    background: #dcdcdc !important;
    border-color: #bbbbbb !important;
    text-shadow: none !important;
    opacity: 1 !important;
  }
`}</style>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={selectedRequestIds.includes(String(r?.id || ""))}
                        onChange={() => toggleSelectedRequestId(String(r?.id || ""))}
                      />
                    </label>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <div style={{ fontWeight: 900 }}>{String(r.player_name || "Player")}</div>
                      <span
                        style={{
                          fontSize: 11,
                          padding: "3px 8px",
                          borderRadius: 999,
                          border: "1px solid rgba(255,255,255,0.10)",
                          background: "rgba(255,255,255,0.05)",
                          opacity: 0.9
                        }}
                      >
                        {String(r.alliance_name || "—")}
                      </span>
                    </div>
                    <div style={{ marginLeft: "auto", fontWeight: 900 }}>{cur}/{req}{done ? " ✅" : ""}</div>
                  </div>

                  <div style={{ opacity: 0.85, marginTop: 6 }}>
                    <span style={{ fontWeight: 700 }}>
                      {typeName(r.achievement_type_id)}
                    </span>
                    {r.option_id ? (
                      <span style={{ opacity: 0.78 }}>
                        {" — " + optionLabel(r.option_id)}
                      </span>
                    ) : null}
                  </div>

                  <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                    <div style={{ opacity: 0.75, fontSize: 12 }}>Count</div>
                    <input
                      className="zombie-input"
                      value={String(r.current_count ?? 0)}
                      disabled={!canAdmin}
                      onChange={(e) => setLocalRequests(r.id, { current_count: Math.max(0, asInt(e.target.value, 0)) })}
                      style={{ padding: "8px 10px", width: 90 }}
                    />
                    <button
                      className="zombie-btn"
                      type="button"
                      style={{
                        padding: "8px 10px",
                        fontSize: 12,
                        minWidth: 52,
                        border: "1px solid rgba(255,255,255,0.12)",
                        background: "rgba(255,255,255,0.04)"
                      }}
                      disabled={!canAdmin}
                      onClick={() => setLocalRequests(r.id, { current_count: cur + 1, status: "in_progress" })}
                    >
                      +1
                    </button>
                    <button
                      className="zombie-btn"
                      type="button"
                      style={{
                        padding: "8px 10px",
                        fontSize: 12,
                        minWidth: 78,
                        border: "1px solid rgba(120,255,120,0.28)",
                        background: "rgba(120,255,120,0.08)"
                      }}
                      disabled={!canAdmin}
                      onClick={() => setLocalRequests(r.id, { current_count: req, status: "completed" })}
                    >
                      Set ✅
                    </button>

                    <div style={{ opacity: 0.75, fontSize: 12 }}>Status</div>
                    <select className="zombie-input" value={String(r.status || "submitted")} disabled={!canAdmin} onChange={(e) => setLocalRequests(r.id, { status: e.target.value })} style={{ padding: "8px 10px" }}>
                      <option value="submitted">submitted</option>
                      <option value="in_progress">in_progress</option>
                      <option value="completed">completed</option>
                      <option value="denied">denied</option>
                    </select>

                    <button
                      className="zombie-btn"
                      type="button"
                      style={{
                        padding: "8px 12px",
                        fontWeight: 900,
                        minWidth: 72,
                        border: "1px solid rgba(255,255,255,0.18)",
                        background: "rgba(255,255,255,0.08)"
                      }}
                      disabled={!canAdmin}
                      onClick={() => saveRequestRow(r)}
                    >
                      Save
                    </button>
                  </div>

                  <div style={{ marginTop: 10 }}>
                    <textarea
                      className="zombie-input"
                      value={String(r.notes || "")}
                      disabled={!canAdmin}
                      onChange={(e) => setLocalRequests(r.id, { notes: e.target.value })}
                      placeholder="Notes"
                      style={{ padding: "10px 12px", width: "100%", minHeight: 60 }}
                    />
                  </div>

                  <div style={{ opacity: 0.6, fontSize: 12, marginTop: 8 }}>
                    <span>Created: {String(r.created_at || "—")}</span>
                    {r.completed_at ? (
                      <span style={{ marginLeft: 8, color: "rgba(120,255,120,0.88)" }}>
                        Completed: {String(r.completed_at)}
                      </span>
                    ) : null}
                  </div>
                </div>
              );
            })}
            {!loading && filteredRequests.length === 0 ? <div style={{ opacity: 0.75 }}>No matching requests.</div> : null}
          </div>
        </div>
      ) : null}

      {tab === "types" ? (
        <div className="zombie-card" style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 900 }}>Achievement Types</div>

          <div style={{ marginTop: 10, display: "grid", gap: 10, maxWidth: 760 }}>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <input className="zombie-input" value={newTypeName} onChange={(e) => setNewTypeName(e.target.value)} placeholder="Type name (SWP Weapon)" style={{ padding: "10px 12px", minWidth: 240, flex: 1 }} />
              <input className="zombie-input" value={newTypeKind} onChange={(e) => setNewTypeKind(e.target.value)} placeholder="kind (count)" style={{ padding: "10px 12px", width: 140 }} />
              <input className="zombie-input" value={newTypeReqCount} onChange={(e) => setNewTypeReqCount(e.target.value)} placeholder="required_count" style={{ padding: "10px 12px", width: 140 }} />
              <label style={{ display: "flex", gap: 8, alignItems: "center", opacity: 0.85 }}>
                <input type="checkbox" checked={newTypeRequiresOption} onChange={(e) => setNewTypeRequiresOption(e.target.checked)} />
                requires option
              </label>
              <button className="zombie-btn" style={{ padding: "10px 12px", fontWeight: 900 }} disabled={!canAdmin} onClick={createType}>Create</button>
            </div>

            <div style={{ opacity: 0.65, fontSize: 12 }}>
              For Governor Rotations set required_count=3 and requires option unchecked.
            </div>
          </div>

          <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
            {types.map((t) => (
              <div key={String(t.id)} style={{ padding: 10, borderRadius: 12, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(0,0,0,0.20)" }}>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <div style={{ fontWeight: 900 }}>{String(t.name || t.id)}</div>
                  <div style={{ marginLeft: "auto", opacity: 0.75, fontSize: 12 }}>{String(t.kind || "")}</div>
                </div>

                <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <div style={{ opacity: 0.75, fontSize: 12 }}>required_count</div>
                  <input className="zombie-input" value={String(t.required_count ?? 1)} disabled={!canAdmin} onChange={(e) => setTypes((p) => p.map((x) => x.id===t.id ? { ...x, required_count: asInt(e.target.value, 1) } : x))} style={{ padding: "8px 10px", width: 100 }} />
                  <label style={{ display: "flex", gap: 8, alignItems: "center", opacity: 0.85 }}>
                    <input type="checkbox" checked={t.requires_option === true} disabled={!canAdmin} onChange={(e) => setTypes((p) => p.map((x) => x.id===t.id ? { ...x, requires_option: e.target.checked } : x))} />
                    requires option
                  </label>
                  <label style={{ display: "flex", gap: 8, alignItems: "center", opacity: 0.85 }}>
                    <input type="checkbox" checked={t.active === true} disabled={!canAdmin} onChange={(e) => setTypes((p) => p.map((x) => x.id===t.id ? { ...x, active: e.target.checked } : x))} />
                    active
                  </label>

                  <button className="zombie-btn" style={{ padding: "8px 10px", fontWeight: 900 }} disabled={!canAdmin} onClick={() => saveType(t)}>Save</button>
                  <button className="zombie-btn" style={{ padding: "8px 10px" }} disabled={!canAdmin} onClick={() => disableType(String(t.id))}>Disable</button>

                  <button className="zombie-btn" style={{ padding: "8px 10px" }} onClick={() => { setSelectedTypeId(String(t.id)); setTab("options"); }}>
                    Edit Options
                  </button>
                </div>
              </div>
            ))}
            {!loading && types.length === 0 ? <div style={{ opacity: 0.75 }}>No types yet.</div> : null}
          </div>
        </div>
      ) : null}

      {tab === "options" ? (
        <div className="zombie-card" style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 900 }}>Options (Weapons / Sub-choices)</div>

          <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ opacity: 0.75, fontSize: 12 }}>Type</div>
            <select className="zombie-input" value={selectedTypeId} onChange={(e) => setSelectedTypeId(e.target.value)} style={{ padding: "10px 12px", minWidth: 280 }}>
              {types.map((t) => (
                <option key={String(t.id)} value={String(t.id)}>{String(t.name || t.id)}</option>
              ))}
            </select>

            <input className="zombie-input" value={newOptLabel} onChange={(e) => setNewOptLabel(e.target.value)} placeholder="Option label (Rail Gun)" style={{ padding: "10px 12px", minWidth: 220, flex: 1 }} />
            <input className="zombie-input" value={newOptSort} onChange={(e) => setNewOptSort(e.target.value)} placeholder="sort" style={{ padding: "10px 12px", width: 120 }} />
            <button className="zombie-btn" style={{ padding: "10px 12px", fontWeight: 900 }} disabled={!canAdmin} onClick={createOption}>Add</button>
          </div>

          <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
            {(optionsByType[String(selectedTypeId || "")] || []).map((o) => (
              <div key={String(o.id)} style={{ padding: 10, borderRadius: 12, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(0,0,0,0.20)" }}>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <input className="zombie-input" value={String(o.label || "")} disabled={!canAdmin} onChange={(e) => setOptions((p) => p.map((x) => x.id===o.id ? { ...x, label: e.target.value } : x))} style={{ padding: "8px 10px", minWidth: 240, flex: 1 }} />
                  <div style={{ opacity: 0.75, fontSize: 12 }}>sort</div>
                  <input className="zombie-input" value={String(o.sort ?? 0)} disabled={!canAdmin} onChange={(e) => setOptions((p) => p.map((x) => x.id===o.id ? { ...x, sort: asInt(e.target.value, 0) } : x))} style={{ padding: "8px 10px", width: 100 }} />
                  <label style={{ display: "flex", gap: 8, alignItems: "center", opacity: 0.85 }}>
                    <input type="checkbox" checked={o.active === true} disabled={!canAdmin} onChange={(e) => setOptions((p) => p.map((x) => x.id===o.id ? { ...x, active: e.target.checked } : x))} />
                    active
                  </label>
                  <button className="zombie-btn" style={{ padding: "8px 10px", fontWeight: 900 }} disabled={!canAdmin} onClick={() => saveOption(o)}>Save</button>
                  <button className="zombie-btn" style={{ padding: "8px 10px" }} disabled={!canAdmin} onClick={() => disableOption(String(o.id))}>Disable</button>
                </div>
              </div>
            ))}
            {!loading && (!selectedTypeId || (optionsByType[String(selectedTypeId)] || []).length === 0) ? <div style={{ opacity: 0.75 }}>No options for this type yet.</div> : null}
          </div>
        </div>
      ) : null}

      {tab === "access" ? (
        <div className="zombie-card" style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 900 }}>Access (Helpers)</div>
          <div style={{ opacity: 0.7, fontSize: 12, marginTop: 6 }}>
            Grant a helper Supabase user_id view/edit access for the tracker page.
          </div>

          <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <input className="zombie-input" value={newAccessUserId} onChange={(e) => setNewAccessUserId(e.target.value)} placeholder="helper user_id (UUID)" style={{ padding: "10px 12px", minWidth: 320, flex: 1 }} />
            <label style={{ display: "flex", gap: 8, alignItems: "center", opacity: 0.85 }}>
              <input type="checkbox" checked={newAccessCanView} onChange={(e) => setNewAccessCanView(e.target.checked)} />
              can_view
            </label>
            <label style={{ display: "flex", gap: 8, alignItems: "center", opacity: 0.85 }}>
              <input type="checkbox" checked={newAccessCanEdit} onChange={(e) => setNewAccessCanEdit(e.target.checked)} />
              can_edit
            </label>
            <button className="zombie-btn" style={{ padding: "10px 12px", fontWeight: 900 }} disabled={!canAdmin} onClick={upsertAccess}>Save</button>
          </div>

          <div style={{ marginTop: 12, display: "grid", gap: 10 }}>
            {access.map((a) => (
              <div key={String(a.id || (a.user_id + "_" + a.state_code))} style={{ padding: 10, borderRadius: 12, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(0,0,0,0.20)" }}>
                <div style={{ fontWeight: 900 }}>user_id: {String(a.user_id || "—")}</div>
                <div style={{ marginTop: 8, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <label style={{ display: "flex", gap: 8, alignItems: "center", opacity: 0.85 }}>
                    <input type="checkbox" checked={a.can_view === true} disabled={!canAdmin} onChange={(e) => setAccess((p) => p.map((x) => (x===a ? { ...x, can_view: e.target.checked } : x)))} />
                    can_view
                  </label>
                  <label style={{ display: "flex", gap: 8, alignItems: "center", opacity: 0.85 }}>
                    <input type="checkbox" checked={a.can_edit === true} disabled={!canAdmin} onChange={(e) => setAccess((p) => p.map((x) => (x===a ? { ...x, can_edit: e.target.checked } : x)))} />
                    can_edit
                  </label>
                  <button className="zombie-btn" style={{ padding: "8px 10px", fontWeight: 900 }} disabled={!canAdmin} onClick={() => saveAccessRow(a)}>Save</button>
                  <button className="zombie-btn" style={{ padding: "8px 10px" }} disabled={!canAdmin} onClick={() => removeAccess(a)}>Remove</button>
                </div>
              </div>
            ))}
            {!loading && access.length === 0 ? <div style={{ opacity: 0.75 }}>No helper access rows yet.</div> : null}
          </div>
        </div>
      ) : null}

      {tab === "export" ? (
        <div className="zombie-card" style={{ marginTop: 12 }}>
          <div style={{ fontWeight: 900 }}>Export / Import</div>
          <div style={{ opacity: 0.7, fontSize: 12, marginTop: 6 }}>
            Safe exports (copy JSON). Imports are intentionally limited to PATCH operations to avoid corrupting data.
          </div>

          <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="zombie-btn" style={{ padding: "10px 12px" }} onClick={() => copyJson({ version: 1, exportedUtc: nowUtc(), state_code: stateCode, types }, "types")}>Copy Types JSON</button>
            <button className="zombie-btn" style={{ padding: "10px 12px" }} onClick={() => copyJson({ version: 1, exportedUtc: nowUtc(), state_code: stateCode, options }, "options")}>Copy Options JSON</button>
            <button className="zombie-btn" style={{ padding: "10px 12px" }} onClick={() => copyJson({ version: 1, exportedUtc: nowUtc(), state_code: stateCode, requests }, "requests")}>Copy Requests JSON</button>
            <button className="zombie-btn" style={{ padding: "10px 12px" }} onClick={() => copyJson({ version: 1, exportedUtc: nowUtc(), state_code: stateCode, access }, "access")}>Copy Access JSON</button>
          </div>

          <div style={{ marginTop: 12, opacity: 0.75, fontSize: 12 }}>
            Importing full datasets is intentionally not enabled yet (to avoid duplicate/invalid rows).
            If you want bulk-import for types/options later, we’ll add a strict validator first.
          </div>
        </div>
      ) : null}
    </div>
  );
}


























































